import React from "react";
import PropTypes from "prop-types";
import { Drawer, Icon, Input, Button, Spin, message } from "antd";
import moment from "moment";
import CompanyLosService from "../../modules/companies/services/company-los-services";
import "styles/portfolio-credit-remarks.css";

const { TextArea } = Input;
const MAX_COMMENT_LENGTH = 1000;

function getInitials(name) {
    if (!name) {
        return "?";
    }
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
}

class PortfolioCreditRemarksDrawer extends React.Component {
    state = {
        remarks: [],
        loading: false,
        saving: false,
        newComment: "",
    };

    componentDidUpdate(prevProps) {
        const opened =
            this.props.visible &&
            (!prevProps.visible ||
                prevProps.instanceId !== this.props.instanceId ||
                prevProps.remarkType !== this.props.remarkType);
        if (opened) {
            this.fetchRemarks();
        }
        if (!this.props.visible && prevProps.visible) {
            this.setState({ remarks: [], newComment: "" });
        }
    }

    fetchRemarks = () => {
        const { caseId, instanceId, remarkType } = this.props;
        if (!caseId || instanceId == null || !remarkType) {
            return;
        }
        this.setState({ loading: true });
        CompanyLosService.portfolioInstanceRemarks(caseId, instanceId, remarkType)
            .then(({ data }) => {
                const remarks = Array.isArray(data) ? data : [];
                this.setState({ remarks, loading: false });
            })
            .catch(error => {
                const status = error && error.response && error.response.status;
                if (status === 404) {
                    this.setState({ remarks: [], loading: false });
                    return;
                }
                message.error("Failed to load credit remarks", 2);
                this.setState({ remarks: [], loading: false });
            });
    };

    handleSaveComment = () => {
        const { caseId, instanceId, remarkType, onRemarkSaved, stageId } = this.props;
        if (Number(stageId) !== 8) {
            return;
        }
        const { newComment } = this.state;
        const trimmed = (newComment || "").trim();

        if (!trimmed) {
            message.warning("Please enter a comment", 2);
            return;
        }
        if (trimmed.length > MAX_COMMENT_LENGTH) {
            message.warning(`Comment cannot exceed ${MAX_COMMENT_LENGTH} characters`, 2);
            return;
        }

        this.setState({ saving: true });
        CompanyLosService.postPortfolioInstanceRemark(caseId, instanceId, {
            remark_type: remarkType,
            remark: trimmed,
        })
            .then(() => {
                message.success("Comment saved", 2);
                this.setState({ newComment: "", saving: false });
                this.fetchRemarks();
                if (onRemarkSaved) {
                    onRemarkSaved();
                }
            })
            .catch(() => {
                message.error("Failed to save comment", 2);
                this.setState({ saving: false });
            });
    };

    renderRemarkCard = remark => {
        const formattedDate = remark.create_date
            ? moment(remark.create_date).format("DD-MMM-YYYY hh:mm A")
            : "--";

        return (
            <div className="portfolio-remark-card" key={remark.id}>
                <div className="portfolio-remark-card-header">
                    <div className="portfolio-remark-author">
                        <div className="portfolio-remark-avatar">
                            {getInitials(remark.created_by_name)}
                        </div>
                        <div className="portfolio-remark-author-name">
                            {remark.created_by_name || "--"}
                        </div>
                    </div>
                    <div className="portfolio-remark-date">{formattedDate}</div>
                </div>
                <div className="portfolio-remark-text">{remark.remark}</div>
                <div className="portfolio-remark-footer">
                    Added by {remark.created_by_name || "--"} · {formattedDate}
                </div>
            </div>
        );
    };

    render() {
        const { visible, onClose, category, stageId } = this.props;
        const { remarks, loading, saving, newComment } = this.state;
        const canAddComment = Number(stageId) === 8;
        const commentLabel =
            remarks.length === 1 ? "1 comment" : `${remarks.length} comments`;

        return (
            <Drawer
                className="portfolio-credit-remarks-drawer"
                title={null}
                width={480}
                placement="right"
                closable={false}
                onClose={onClose}
                visible={visible}
                destroyOnClose
            >
                <div className="portfolio-credit-remarks-header">
                    <div className="d-flex justify-content-space-between align-items-start">
                        <div>
                            <h3>
                                <Icon type="message" style={{ marginRight: 8 }} />
                                Credit Remarks
                            </h3>
                            <p>
                                Portfolio Category: <strong>{category}</strong> · {commentLabel}
                            </p>
                        </div>
                        <Button type="link" icon="close" onClick={onClose} style={{ padding: 0 }} />
                    </div>
                </div>

                <div className="portfolio-credit-remarks-timeline">
                    <Spin spinning={loading}>
                        {remarks.length ? (
                            remarks.map(this.renderRemarkCard)
                        ) : (
                            !loading && (
                                <div className="portfolio-credit-remarks-empty">
                                    No remarks yet for this category.
                                </div>
                            )
                        )}
                    </Spin>
                </div>

                {canAddComment && (
                    <div className="portfolio-credit-remarks-compose">
                        <label>ADD NEW COMMENT</label>
                        <TextArea
                            rows={3}
                            placeholder="Add a short justification..."
                            value={newComment}
                            maxLength={MAX_COMMENT_LENGTH}
                            onChange={e => this.setState({ newComment: e.target.value })}
                        />
                        <div className="portfolio-credit-remarks-compose-footer">
                            <span className="portfolio-credit-remarks-compose-meta">
                                <Icon type="lock" style={{ marginRight: 4 }} />
                                Logged with user, timestamp &amp; case ID
                            </span>
                            <div>
                                <span style={{ fontSize: 11, color: "#bfbfbf", marginRight: 12 }}>
                                    {newComment.length}/{MAX_COMMENT_LENGTH}
                                </span>
                                <Button
                                    type="primary"
                                    loading={saving}
                                    onClick={this.handleSaveComment}
                                >
                                    Save Comment
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Drawer>
        );
    }
}

PortfolioCreditRemarksDrawer.propTypes = {
    visible: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    caseId: PropTypes.number,
    instanceId: PropTypes.number,
    remarkType: PropTypes.string,
    category: PropTypes.string,
    stageId: PropTypes.number,
    onRemarkSaved: PropTypes.func,
};

export default PortfolioCreditRemarksDrawer;
